// National.js
import React, { useEffect, useState } from "react";
import { Divider, Button, Popover, Tag, Badge, Skeleton } from "antd";
import { FilterOutlined, FilterFilled } from "@ant-design/icons";
import CustomCard from "../../blocks/Card/Card";
import MetroMap from "./MetroMap";
import MetroTable from "./MetroTable";
import MetroLangLatData from "./data.json";
import "./metroTable.css";
import { isMobileOnly } from "react-device-detect";
import MapKey from "../../assets/images/map-key.png";
import { formatNumberWithCommas } from "../../helpers/formatters";
import { useNational } from "./useNational";

const National = () => {
  const { data: metroJsonData, isLoading, isError } = useNational();
  const [fullMetroData, setFullMetroData] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});

  useEffect(() => {
    if (metroJsonData) {
      const mergedArray = metroJsonData.map((item) => {
        const match = MetroLangLatData.find(
          (second) =>
            second?.cbsa_code?.toString() === item?.cbsa_code?.toString()
        );
        return {
          ...item,
          latitude: match?.latitude,
          longitude: match?.longitude,
          lastyearchange: (
            (parseFloat(item.lastyearchange) || 0) * 100
          ).toFixed(2),
        };
      });
      setFullMetroData(mergedArray);
    }
  }, [metroJsonData]);

  const applyFilters = (data, filters) => {
    return data.filter((record) => {
      return Object.entries(filters).every(([key, filter]) => {
        if (!filter || filter.length === 0) return true;
        if (key === "region") return filter.includes(record[key]);
        const [min, max] = filter;
        const value = parseFloat(record[key]);
        return value >= min && value <= max;
      });
    });
  };

  const filteredData = applyFilters(fullMetroData, activeFilters);

  const handleFilterChange = (newFilters) => {
    const cleanedFilters = Object.fromEntries(
      Object.entries(newFilters).filter(([_, value]) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return value !== null && value !== undefined;
      })
    );
    setActiveFilters(cleanedFilters);
  };

  if (isLoading) return <Skeleton active />;
  if (isError) return <div>Error loading data...</div>;

  const renderFilterTags = () => {
    return Object.entries(activeFilters).map(([key, value]) => {
      const label = {
        median: "Median",
        forecast: "Forecast",
        percentrising: "Percent Rising",
        percentfalling: "Percent Falling",
        bestforecast: "Best Forecast",
        worstforecast: "Worst Forecast",
        lastyearchange: "Last Year Change",
        region: "Region",
      }[key];

      if (key === "region") {
        return (
          <Tag key={key} color="blue">
            {`${label}: ${value.join(", ")}`}
          </Tag>
        );
      }

      const [min, max] = value;
      const formattedValue =
        key === "median"
          ? `$${formatNumberWithCommas(min)} - $${formatNumberWithCommas(max)}`
          : `${min}% - ${max}%`;

      return (
        <Tag key={key} color="blue">
          {`${label}: ${formattedValue}`}
        </Tag>
      );
    });
  };

  const filterCount = Object.keys(activeFilters).length;

  return (
    <div className="metro-map">
      <CustomCard
        title={
          <div className="metro-map-card-title">
            <h3 className="title-2">National Metro Map</h3>
            <img
              src={MapKey}
              alt="map key"
              style={{ width: isMobileOnly ? "220px" : "350px" }}
            />
            <Tag color="processing">Based on Last Year Change</Tag>
          </div>
        }
      >
        <Popover
          content={
            filterCount > 0 ? (
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                {renderFilterTags()}
                <Button
                  type="primary"
                  onClick={() => setActiveFilters({})}
                  style={{ marginTop: "10px" }}
                >
                  Reset Filters
                </Button>
              </div>
            ) : (
              <div>No Filters Applied</div>
            )
          }
          trigger="click"
          placement="bottomRight"
        >
          <Badge count={filterCount} style={{ backgroundColor: "#7cb305" }}>
            <Button
              type="text"
              icon={
                filterCount > 0 ? (
                  <FilterFilled style={{ color: "#7cb305" }} />
                ) : (
                  <FilterOutlined />
                )
              }
              style={{
                border: "1px solid grey",
                width: "140px",
                borderRadius: "5px",
                marginBottom: "14px",
                height: "32px",
                textAlign: "left",
                paddingLeft: "18px",
              }}
            >
              Filters
            </Button>
          </Badge>
        </Popover>

        <MetroMap selectedMetros={filteredData} />
        <Divider />
        <MetroTable
          data={fullMetroData}
          filteredData={filteredData}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
        />
      </CustomCard>
    </div>
  );
};

export default National;
